/**
 * Component showing the information about an API key and its Webhooks.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2026
 */
import { deleteApiKey, deleteWebhook, fetchApiKeyList } from '@/api'
import { AppRouteUrl } from '@/AppRoute'
import IconButton from '@/components/Button/IconButton'
import ModalDialog from '@/components/ModalDialog'
import { logger } from '@/config/logger'
import ProgressIconButton from '@/pages/TaskList/ProgressIconButton'
import { ApiKey, Webhook } from '@amplify3/duplo-back-end'
import { useState } from 'react'
import { MdDeleteForever } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import './ApiKeyComponent.css'

interface ApiKeyComponentProps {
  apiKey: ApiKey
}

const ApiKeyComponent = (props: ApiKeyComponentProps) => {
  const [isRevokeProcessing, setRevokeProcess] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const [isConfirmDeleteDialogVisible, setConfirmDeleteDialogVisible] =
    useState(false)

  const creationDate = new Date(props.apiKey.creation_date)
  const lastDate = props.apiKey.last_time_used
    ? new Date(props.apiKey.last_time_used)
    : null

  async function handleDeleteApiKey() {
    setRevokeProcess(true)
    setErrorMsg(undefined)

    try {
      await deleteApiKey({ apiClientId: props.apiKey.client_id })
      await fetchApiKeyList({ forceRefresh: true })
    } catch (error) {
      logger.error(error)
      setErrorMsg('Error when trying to delete API key')
    } finally {
      setRevokeProcess(false)
    }
  }

  return (
    <div className="api-key-component">
      <div className="header">
        <div className="name">{props.apiKey.app_name}</div>
        <div className="date">{`Created ${creationDate.toLocaleDateString()}`}</div>
        {lastDate && (
          <>
            <div className="date">·</div>
            <div className="date">{`Last used ${lastDate.toLocaleDateString()}`}</div>
          </>
        )}
        <ProgressIconButton
          isWaiting={isRevokeProcessing}
          label="Revoke"
          id="revoke"
          isWaitingShowlabel={false}
          onClick={() => setConfirmDeleteDialogVisible(true)}
        />
      </div>

      <div className="client-id-info">
        <div className="client-id-label">Client ID</div>
        <div className="client-id-value">{props.apiKey.client_id}</div>
        <IconButton
          label="Copy"
          id="copy"
          onClick={() => {
            navigator.clipboard.writeText(props.apiKey.client_id)
          }}
        />
      </div>

      <div className="separator" />
      <WebhookList
        api_key_id={props.apiKey.client_id}
        webhookList={props.apiKey.webhooks}
        errorMsg={errorMsg}
        setErrorMsg={setErrorMsg}
      />

      <ModalDialog
        title="Delete API key"
        isVisible={isConfirmDeleteDialogVisible}
        changeVisible={setConfirmDeleteDialogVisible}
        contents={
          <div className="confirm-delete-dialog-content">
            <div>
              Are you sure you want to delete API key{' '}
              <span className="app-name">{props.apiKey.app_name}</span> ?
            </div>
          </div>
        }
        className="confirm-delete-dialog"
        maxWidth="xs"
        showTopRighCloseButton={false}
        showCancelButton={true}
        showOkButton={true}
        onOk={handleDeleteApiKey}
      ></ModalDialog>
    </div>
  )
}

interface WebhookListProps {
  api_key_id: string
  webhookList?: Webhook[]
  setErrorMsg: (msg: string | undefined) => void
  errorMsg?: string
}

const WebhookList = (props: WebhookListProps) => {
  const navigate = useNavigate()

  return (
    <div className="webhook-list">
      <div className="status-tool-bar">
        <IconButton
          label="+ Add webhook"
          id="add-webhook"
          onClick={() =>
            navigate(AppRouteUrl.WebhookCreate, {
              state: { apiKeyId: props.api_key_id },
            })
          }
        />

        {props.errorMsg ? (
          <div className="status-text error">{props.errorMsg}</div>
        ) : (
          <></>
        )}
      </div>

      {props.webhookList?.map((webhook: Webhook) => (
        <WebhookRow
          key={webhook.id}
          webhook={webhook}
          setErrorMsg={props.setErrorMsg}
        />
      ))}
    </div>
  )
}

interface WebhookRowProps {
  webhook: Webhook
  setErrorMsg: (msg: string | undefined) => void
}

const WebhookRow = (props: WebhookRowProps) => {
  const [isDeleting, setDeleting] = useState(false)
  const [isConfirmDeleteDialogVisible, setConfirmDeleteDialogVisible] =
    useState(false)

  async function onDelete(): Promise<void> {
    props.setErrorMsg(undefined)
    setDeleting(true)

    try {
      const response = await deleteWebhook({ webhookId: props.webhook.id })
      logger.info(response.message)
      await fetchApiKeyList({ forceRefresh: true })
    } catch {
      props.setErrorMsg('Error when trying to delete the webhook')
    } finally {
      setConfirmDeleteDialogVisible(false)
    }
  }

  return (
    <div className="webhook-row">
      <div className="name">{props.webhook.name}</div>
      <div className="url">{props.webhook.url}</div>

      <ProgressIconButton
        id="delete-btn"
        isWaiting={isDeleting}
        isWaitingShowlabel={false}
        onClick={() => setConfirmDeleteDialogVisible(true)}
        icon={MdDeleteForever}
      />

      <ModalDialog
        title="Delete API key"
        isVisible={isConfirmDeleteDialogVisible}
        changeVisible={setConfirmDeleteDialogVisible}
        contents={
          <div className="confirm-delete-dialog-content">
            <div>
              Are you sure you want to delete the webhook{' '}
              <span className="app-name">{props.webhook.name}</span> ?
            </div>
          </div>
        }
        className="confirm-delete-dialog"
        maxWidth="xs"
        showTopRighCloseButton={false}
        showCancelButton={true}
        showOkButton={true}
        onOk={onDelete}
      ></ModalDialog>
    </div>
  )
}

export default ApiKeyComponent
